import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getEvalLabelingQueueOptions } from '@/api/generated/@tanstack/react-query.gen';
import { Skeleton } from '@/components/ui/skeleton';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';

export const Route = createFileRoute('/_auth/evals/labeling')({
  component: LabelingPage,
});

function LabelingPage() {
  const query = useQuery(getEvalLabelingQueueOptions());
  const rows = query.data ?? [];
  const next = rows.find((r) => r.tutor_lines && (r.labeled_lines ?? 0) < r.tutor_lines);
  return (
    <EvalShell
      title="Labeling"
      subtitle="Finished runs, least labeled first."
      actions={
        <>
          <Link
            to="/evals/label"
            className="btn-3d h-9 rounded-full px-5 text-[13px] leading-9"
          >
            Rapid labeling
          </Link>
          {next && (
          <Link
            to="/reviews/$roomId"
            params={{ roomId: next.room_id }}
            className="h-9 rounded-full bg-neutral-100 px-4 text-[13px] font-medium leading-9 text-neutral-700 hover:bg-neutral-200"
          >
            Open next session
          </Link>
          )}
        </>
      }
    >
      <Panel>
        {!query.data ? (
          <Skeleton className="h-60 rounded-2xl" />
        ) : !rows.length ? (
          <p className="text-sm text-neutral-400">No finished runs yet.</p>
        ) : (
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-neutral-400">
                <th className="py-1.5 pr-3 font-medium">Session</th>
                <th className="py-1.5 pr-3 font-medium">Learner</th>
                <th className="py-1.5 pr-3 font-medium">Experiment</th>
                <th className="py-1.5 pr-3 font-medium">Labeled</th>
                <th className="py-1.5 font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.run_id} className="border-t border-neutral-100">
                  <td className="py-2 pr-3 font-medium text-neutral-800">{row.fixture_key}</td>
                  <td className="py-2 pr-3 text-neutral-600">
                    {row.persona}
                    <span className="text-neutral-400"> · {row.learner_voice}</span>
                  </td>
                  <td className="py-2 pr-3 text-neutral-500">{row.experiment_name}</td>
                  <td className="min-w-40 py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-neutral-100">
                        <div
                          className="h-full rounded-full bg-[#AAE73D]"
                          style={{ width: `${row.tutor_lines ? ((row.labeled_lines ?? 0) / row.tutor_lines) * 100 : 0}%` }}
                        />
                      </div>
                      <span className="font-mono text-[11px] text-neutral-500">
                        {row.labeled_lines ?? 0}/{row.tutor_lines || '?'}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 text-right">
                    <Link
                      to="/reviews/$roomId"
                      params={{ roomId: row.room_id }}
                      className="text-[12px] font-medium text-neutral-700 hover:underline"
                    >
                      Label
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </EvalShell>
  );
}
